import React, { useState, useRef } from 'react';
import { LoadingSpinner } from 'yankees-react-utils';
import { Col, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { Advance, Player, PlayerReport, useOktaQuery, useCachedOktaQuery, isLhb as isLhbFunc, StrikeZones,
    HeatMaps as HeatMapsApi, Stats } from '../../../../data';
import PitcherHeader from '../PitcherHeader';
import Filters from './Filters';
import DeliveryNotes from './DeliveryNotes';
import RepertoireTable from './RepertoireTable';
import PitchFrequencyTable from './PitchFrequencyTable';
import AdvanceReportNotes from './AdvanceReportNotes';
import PitchLocations from './PitchLocations';
import HeatMaps from './HeatMaps';
import PitchBreak from './PitchBreak';
import TrendsChart from './TrendsChart';
import PitchUsageTable from './PitchUsageTable';
import BarrelBoxes from './BarrelBoxes';
import AttackPlanNotes from './AttackPlanNotes';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import useConfig from '../../../../utils/useConfig';
import useOpposingTeamSelector from '../../../../utils/useOpposingTeamSelector';
import DamageZones from './DamageZones';
import SprayChart from './SprayChart';
import PitchMovementImages from './PitchMovementImages';
import StatNotes from './StatNotes';
import BatTracking from './BatTracking';

const StyledContainer = styled.div`
    width: 95%;
    margin: 12px auto;

    .card-body h4.clearfix {
        text-transform: uppercase;
    }

    .header-wrapper {
        display: flex;
    }

    ul {
        list-style: none;
    }
`;

const PitcherOverview = ({ pitcherId }) => {
    const { UsePreviousYearData } = useConfig();
    const modalContainer = useRef(null);

    const batter = usePlayerSelector();
    const opposingTeam = useOpposingTeamSelector();

    const [year, setYear] = useState(null);
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear() - (UsePreviousYearData ? 1 : 0), 0, 1));
    const [endDate, setEndDate] = useState(new Date());
    const [handedness, setHandedness] = useState(null);

    const { data: pitcher, isFetching: isLoadingPitcher } = useCachedOktaQuery(
        ['player', pitcherId],
        (t) => Player.get(t, { playerId: pitcherId }),
    );

    // eslint-disable-next-line no-nested-ternary
    const isLhb = handedness?.value === 3 ? false : handedness?.value === 2 ? true : isLhbFunc(pitcher, batter);
    const levelId = opposingTeam.LevelID !== 8 ? 7 : 8;
    const isVsBatter = handedness?.value === 1;

    let heatmapYear = year?.value;
    if (!isVsBatter && !window.$IsDugoutApp) heatmapYear = (new Date(endDate)).getFullYear();

    const pitcherOverviewParams = {
        pitcherId,
        batterId: isVsBatter ? batter.PlayerID : null,
        levelId,
        yearId: isVsBatter || window.$IsDugoutApp ? year?.value : null,
        isLhb: isVsBatter ? null : isLhb,
        startDate: isVsBatter || window.$IsDugoutApp ? null : startDate,
        endDate: isVsBatter || window.$IsDugoutApp ? null : endDate,
    };
    const { data: pitcherOverview, isLoading: isLoadingPitcherOverview, isFetched } = useOktaQuery(
        ['pitcherOverview', pitcherOverviewParams],
        (t) => Advance.pitcherOverview(t, pitcherOverviewParams),
        { enabled: !!pitcher && !!batter && !!year && !!handedness },
    );
    const { data: pitchCharacteristics, isLoading: isLoadingPitchCharacteristics } = useOktaQuery(
        ['pitchCharacteristics', pitcherId],
        (t) => PlayerReport.pitchcharacteristics(t, { playerId: pitcherId, mergeFb: false }),
    );
    const { data: szContours, isLoading: isLoadingSzContours } = useOktaQuery(
        ['szContours', levelId, year?.value, isLhb],
        (t) => StrikeZones.getStrikeZoneContours(t, { isLhb, yearId: year?.value, levelId }),
        { enabled: !!year?.value && !!levelId, select: (data) => (pitcher.Throws === 2 ? data.lhp : data.rhp) },
    );
    // eslint-disable-next-line no-nested-ternary
    const damageZonesHeatMapPlayerId = handedness?.value === 1 ? batter.PlayerID : (isLhb ? 2 : 1);
    const { data: damageZonesHeatMap, isLoading: isLoadingDamageZonesHeatMap } = useOktaQuery(
        ['damageZonesHeatMap', damageZonesHeatMapPlayerId],
        (t) => HeatMapsApi.damageZones(t, damageZonesHeatMapPlayerId, { yearId: -2 }),
    );
    const { data: heatmaps, isLoading: isLoadingHeatmaps } = useOktaQuery(
        ['heatmaps-pitchlocationactual', pitcherId, heatmapYear],
        (t) => HeatMapsApi.pitchLocationActual(t, pitcherId, { yearId: heatmapYear }),
        { enabled: !!heatmapYear },
    );
    const { data: zScoreData, isLoading: isLoadingZScoreData } = useOktaQuery(
        ['zScoreData', { isLhb, isLhp: pitcher.Throws === 2, levelid: levelId, yearid: -2 }],
        (t) => Stats.pitchtypebreakdownpitchfxzscore(t, { islhb: isLhb, islhp: pitcher.Throws === 2, levelid: levelId, yearid: -2 }),
        { enabled: !!pitcher },
    );
    const { data: timeThroughOrder, isLoading: isLoadingTimeThroughOrder } = useOktaQuery(
        ['timethroughorder', pitcherId, year?.value, levelId, isLhb],
        (t) => PlayerReport.timethroughorder(t, { pitcherId, yearId: year?.value, levelId, isLhb }),
        { enabled: !!year?.value },
    );

    const { splits, repertoire, lgavgkzon, pitches, kzon, expected, pitchtypeaverages, hbatvbatellipses } = pitcherOverview || {};

    const numFbInRepertoire = repertoire?.reduce((all, stat) => all + ([9, 10].includes(stat.fxtype) ? 1 : 0), 0);

    if (isLoadingPitcher) {
        return <div className="py-5 text-center"> <LoadingSpinner /> </div>;
    }

    if (isLoadingPitcherOverview || isLoadingPitchCharacteristics || !isFetched) {
        return (
            <StyledContainer>
                <Row className="align-items-center">
                    <Col md={6} sm={12}>
                        <PitcherHeader pitcher={pitcher} isLhb={isLhb} batter={handedness?.value === 1 ? batter : null} levelId={opposingTeam.LevelID} />
                    </Col>
                    <Col md={6} sm={12}>
                        <Filters
                            isLhb={isLhb}
                            year={year}
                            setYear={setYear}
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                            handedness={handedness}
                            setHandedness={setHandedness}
                        />
                    </Col>
                </Row>
                <div className="py-5 text-center"> <LoadingSpinner /> </div>
            </StyledContainer>
        );
    }

    return (
        <StyledContainer>
            <Row className="align-items-center">
                <Col md={6} sm={12}>
                    <PitcherHeader
                        pitcher={pitcher}
                        isLhb={isLhb}
                        splits={splits}
                        batter={handedness?.value === 1 ? batter : null}
                        levelId={opposingTeam.LevelID}
                    />
                </Col>
                <Col md={6} sm={12}>
                    <Filters
                        isLhb={isLhb}
                        year={year}
                        setYear={setYear}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        handedness={handedness}
                        setHandedness={setHandedness}
                    />
                </Col>
            </Row>
            <DeliveryNotes isLhb={isLhb} pitcherId={pitcherId} levelId={levelId} />
            <Row>
                <Col sm={12}>
                    <RepertoireTable
                        pitcherId={pitcherId}
                        levelId={levelId}
                        repertoire={repertoire}
                        numFbInRepertoire={numFbInRepertoire}
                        lgavgkzon={lgavgkzon}
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <PitchFrequencyTable
                        isVsBatter={handedness?.value === 1}
                        expected={expected}
                        kzon={kzon}
                        lgavgkzon={lgavgkzon}
                        numFbInRepertoire={numFbInRepertoire}
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <AdvanceReportNotes
                        pitcherId={pitcherId}
                        isLhb={isLhb}
                        levelId={levelId}
                    />
                </Col>
            </Row>
            {levelId !== 8 && (
                <Row>
                    <Col sm={12}>
                        <StatNotes
                            pitcherId={pitcherId}
                            isLhb={isLhb}
                            levelId={levelId}
                        />
                    </Col>
                </Row>
            )}
            <Row>
                <Col sm={12}>
                    <PitchLocations
                        modalContainer={modalContainer}
                        isLhb={isLhb}
                        isLhp={pitcher.Throws === 2}
                        levelId={levelId}
                        pitches={pitches}
                        repertoire={repertoire}
                        yearId={year}
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <HeatMaps
                        isLoading={isLoadingHeatmaps}
                        heatmaps={heatmaps}
                        yearId={year?.value}
                        isLhb={isLhb}
                        pitches={pitches}
                        repertoire={repertoire}
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <PitchBreak
                        isVsBatter={handedness?.value === 1}
                        pitches={pitches}
                        pitchCharacteristics={pitchCharacteristics}
                        throws={pitcher.Throws}
                        pitcherId={pitcherId}
                        yearId={year?.value}
                        isLhb={isLhb}
                        isLhp={pitcher.Throws === 2}
                        levelId={levelId}
                        lgavgkzon={lgavgkzon}
                        repertoire={repertoire}
                        isLoading={isLoadingZScoreData}
                        zScoreData={zScoreData}
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <BatTracking
                        pitches={pitchtypeaverages}
                        isLhb={isLhb}
                        isLhp={pitcher.Throws === 2}
                        repertoire={repertoire}
                        hbatVbatEllipses={hbatvbatellipses}
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <PitchUsageTable
                        isLoading={isLoadingTimeThroughOrder}
                        timeThroughOrder={timeThroughOrder}
                        lgavgkzon={lgavgkzon}
                        numFbInRepertoire={numFbInRepertoire}
                        isStartingPitcher={pitcher?.PrimaryPosition === 11 || pitcher?.IsTwoWay === true}
                        repertoire={repertoire}
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <TrendsChart
                        pitches={pitches}
                        isStartingPitcher={pitcher?.PrimaryPosition === 11 || pitcher?.IsTwoWay === true}
                        repertoire={repertoire}
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <BarrelBoxes
                        isLhb={isLhb}
                        pitcherId={pitcherId}
                        levelId={levelId}
                    />
                </Col>
            </Row>
            <AttackPlanNotes isLhb={isLhb} pitcherId={pitcherId} levelId={levelId} />
            <Row>
                <Col sm={12}>
                    <DamageZones
                        isLoading={isLoadingDamageZonesHeatMap || isLoadingSzContours}
                        isLhp={pitcher.Throws === 2}
                        isLhb={isLhb}
                        repertoire={repertoire}
                        pitches={pitches}
                        szContours={szContours}
                        damageZonesHeatMap={damageZonesHeatMap}
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <SprayChart isLhb={isLhb} pitches={pitches} />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <PitchMovementImages
                        levelId={opposingTeam.LevelIDData}
                        pitcherId={pitcherId}
                        repertoire={repertoire}
                        pitchCharacteristics={pitchCharacteristics}
                        isLhp={pitcher.Throws === 2}
                    />
                </Col>
            </Row>
        </StyledContainer>
    );
};

export default PitcherOverview;
